import { API } from "@swivl-mono/api-helper";
import { CreateLogger, DateHelper } from "@swivl-mono/helpers";
import {
  EscalationInterface,
  Log,
  NotificationBaseInterface,
  NotificationInterface,
  NotificationType,
  ToastNotificationInterface,
} from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";
import PendingUpdatesModel, { PendingUpdateType } from "../Pending/Pending.Model";

const log = CreateLogger(Log.NOTIFICATIONS, "Notifications.Model");
interface StoreInterface {
  activeNotifications?: NotificationInterface[];
  allNotifications?: NotificationInterface[];
  clearedEscalationNotifications?: number[]; // EscalationIds that have been cleared
}
const BlankState: StoreInterface = {
  clearedEscalationNotifications: [],
};
function fixDates(item: NotificationInterface) {
  item.updatedAt = DateHelper.normalize(item.updatedAt);
  item.createdAt = DateHelper.normalize(item.createdAt);
  if (item.acknowledgedAt) {
    item.acknowledgedAt = DateHelper.normalize(item.acknowledgedAt);
  }

  return item;
}
export default class NotificationsModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  private static setState = NotificationsModel.useState.setState;

  static get state() {
    return NotificationsModel.useState.getState();
  }
  static reset() {
    NotificationsModel.setState({ ...BlankState });
  }

  static async loadNotifications(force: boolean = false, activeOnly: boolean = true) {
    if (!force && NotificationsModel.state.allNotifications) {
      return;
    }

    const notifications = await API.Notifications.getNotifications(Member.orgId, Member.id!, activeOnly, 999);
    if (!notifications) {
      return;
    }
    this.updateNotifications(notifications);
  }

  static updateNotifications(notifications: NotificationInterface[]) {
    const fixedNotifications = notifications.map((item) => fixDates(item));

    // Add notifications to all notifications and make sure they are sorted by createdAt and are unique
    let allNotifications = NotificationsModel.state.allNotifications || [];
    const uniqueNotifications = new Map<number, NotificationInterface>();
    allNotifications.forEach((item) => {
      uniqueNotifications.set(item.id, item);
    });
    fixedNotifications.forEach((item) => {
      if (uniqueNotifications.has(item.id)) {
        // If the notification already exists, update it
        const existingNotification = uniqueNotifications.get(item.id);
        if (existingNotification && item.createdAt > existingNotification.createdAt) {
          uniqueNotifications.set(item.id, item);
        }
      } else {
        uniqueNotifications.set(item.id, item);
      }
    });
    allNotifications = Array.from(uniqueNotifications.values()).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    const activeNotifications = allNotifications.filter((item) => !item.acknowledged);
    log("## NotificationsModel.updateNotifications", { allNotifications, activeNotifications });
    NotificationsModel.setState({
      activeNotifications: activeNotifications,
      allNotifications: allNotifications,
    });
    notifications.forEach((notification) => {
      PendingUpdatesModel.removePendingUpdate(PendingUpdateType.NOTIFICATION_ACKNOWLEDGED, notification.id);
    });
  }

  static async acknowledgeNotification(notification: NotificationInterface) {
    if (!notification || !notification.id) {
      return;
    }
    PendingUpdatesModel.addPendingUpdate(PendingUpdateType.NOTIFICATION_ACKNOWLEDGED, notification.id);
    notification.acknowledged = true;
    notification.acknowledgedAt = new Date();
    if (notification.type === NotificationType.TOAST) {
      this.updateNotifications([notification]);
      return;
    }

    const acknowledged = await API.Notifications.acknowledge(Member.orgId, notification.id);
    if (acknowledged) {
      this.updateNotifications([acknowledged]);
    }
  }

  static async createToastNotification(message: string, title?: string) {
    let notification: ToastNotificationInterface = {
      type: NotificationType.TOAST,
      id: Math.floor(Math.random() * 100000) + 1,
      memberId: Member.id!,
      acknowledged: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      orgId: Member.orgId,
      data: { message: message, title: title },
    };
    this.updateNotifications([notification]);
  }

  // This is called whenever an escalation is accepted, so we can acknowledge the notification for that escalation or keep it up in case it is still relevant.
  static escalationAcceptedCallback(escalation: EscalationInterface) {
    if (escalation.assignedMemberId !== Member.id) {
      return; // If assigned agent !== current member, then do not call the acknowledge method
    }
    // First check if we even have a notification for this escalation
    const notification = NotificationsModel.state.allNotifications?.find(
      (n) => n.type === NotificationType.ESCALATION && n.data?.id === escalation.id,
    );
    if (!notification) {
      return; // If no notification, then do not call the acknowledge method
    }
    NotificationsModel.acknowledgeNotification(notification);
  }

  static escalationIgnoredCallback(escalation: EscalationInterface) {
    const notification = NotificationsModel.state.allNotifications?.find(
      (n) => n.type === NotificationType.ESCALATION && n.data?.id === escalation.id,
    );
    if (!notification) {
      return; // If no notification, then do not call the acknowledge method
    }
    NotificationsModel.acknowledgeNotification(notification);
  }
}
